import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Link2, KeyRound, Loader2 } from 'lucide-react';
import ApiService from '../../services/ApiService';
import toast from 'react-hot-toast';

const ConnectWooCommerceModal = ({ isOpen, onClose, onManualConnected }) => {
  const [tab, setTab] = useState('app-auth'); // 'app-auth' | 'manual'
  const [storeUrl, setStoreUrl] = useState('');
  const [consumerKey, setConsumerKey] = useState('');
  const [consumerSecret, setConsumerSecret] = useState('');
  const [submitting, setSubmitting] = useState(false);

  if (!isOpen) return null;

  const handleAppAuth = async () => {
    if (!storeUrl.trim()) return toast.error('Enter your store URL first.');
    setSubmitting(true);
    try {
      const data = await ApiService.getWooCommerceConnectUrl(storeUrl.trim());
      const authorizeUrl = data?.authorize_url;
      if (authorizeUrl && typeof authorizeUrl === 'string') {
        // Must be an absolute http(s) URL — assigning a relative or malformed
        // value throws "Failed to set the 'href' property on 'Location'".
        let parsed;
        try {
          parsed = new URL(authorizeUrl);
        } catch {
          throw new Error('Invalid authorize URL returned by the server.');
        }
        if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
          throw new Error('Authorize URL must use http or https.');
        }
        if (data.state) {
          sessionStorage.setItem('wc_auth_state', data.state);
        }
        window.location.assign(authorizeUrl);
        return;
      }
      throw new Error(data?.message || 'Could not start the connection.');
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Failed to start WooCommerce connection.';
      toast.error(typeof msg === 'string' ? msg : 'Failed to start WooCommerce connection.');
      setSubmitting(false);
    }
  };

  const handleManual = async () => {
    if (!storeUrl.trim() || !consumerKey.trim() || !consumerSecret.trim()) {
      return toast.error('All three fields are required.');
    }
    setSubmitting(true);
    try {
      const data = await ApiService.connectWooCommerceManual({
        store_url: storeUrl.trim(),
        consumer_key: consumerKey.trim(),
        consumer_secret: consumerSecret.trim(),
      });
      if (data?.success) {
        toast.success('WooCommerce store connected!');
        const account = data.accounts?.[0] || data.account;
        onManualConnected(account);
      } else {
        throw new Error(data?.message || 'Connection failed — check your keys.');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to connect WooCommerce.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/60 backdrop-blur-sm"
          onClick={!submitting ? onClose : undefined}
        />
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-md bg-white border border-gray-200 rounded-[2rem] overflow-hidden shadow-2xl"
        >
          <button
            onClick={onClose}
            disabled={submitting}
            className="absolute right-6 top-6 p-2 hover:bg-gray-100 rounded-xl disabled:opacity-50"
          >
            <X className="h-5 w-5 text-gray-400" />
          </button>

          <div className="p-8 pt-10">
            <h3 className="text-2xl font-black text-gray-900 mb-2 tracking-tight">Connect WooCommerce</h3>
            <p className="text-gray-500 font-medium mb-6 text-sm">
              Link your store so the agent can answer questions using live product data.
            </p>

            <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6">
              <button
                onClick={() => setTab('app-auth')}
                className={`flex-1 py-2 rounded-lg text-xs font-bold ${
                  tab === 'app-auth' ? 'bg-white shadow-sm' : 'text-gray-400'
                }`}
              >
                Connect
              </button>
              <button
                onClick={() => setTab('manual')}
                className={`flex-1 py-2 rounded-lg text-xs font-bold ${
                  tab === 'manual' ? 'bg-white shadow-sm' : 'text-gray-400'
                }`}
              >
                Enter API keys
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Store URL</label>
                <div className="mt-1.5 flex items-center gap-2 px-4 py-3 border border-gray-200 rounded-xl">
                  <Link2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <input
                    value={storeUrl}
                    onChange={(e) => setStoreUrl(e.target.value)}
                    placeholder="https://yourstore.com"
                    className="flex-1 outline-none text-sm"
                  />
                </div>
              </div>

              {tab === 'manual' && (
                <>
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Consumer key</label>
                    <div className="mt-1.5 flex items-center gap-2 px-4 py-3 border border-gray-200 rounded-xl">
                      <KeyRound className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <input
                        value={consumerKey}
                        onChange={(e) => setConsumerKey(e.target.value)}
                        placeholder="ck_..."
                        className="flex-1 outline-none text-sm"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-500 uppercase tracking-wide">Consumer secret</label>
                    <div className="mt-1.5 flex items-center gap-2 px-4 py-3 border border-gray-200 rounded-xl">
                      <KeyRound className="h-4 w-4 text-gray-400 flex-shrink-0" />
                      <input
                        type="password"
                        value={consumerSecret}
                        onChange={(e) => setConsumerSecret(e.target.value)}
                        placeholder="cs_..."
                        className="flex-1 outline-none text-sm"
                      />
                    </div>
                  </div>
                  <p className="text-[11px] text-gray-400">
                    Generate these in WordPress: WooCommerce → Settings → Advanced → REST API.
                  </p>
                </>
              )}

              <button
                onClick={tab === 'app-auth' ? handleAppAuth : handleManual}
                disabled={submitting}
                className="w-full flex items-center justify-center gap-2 px-6 py-4 bg-black text-white font-bold rounded-2xl hover:bg-gray-800 disabled:opacity-60"
              >
                {submitting ? <Loader2 className="h-5 w-5 animate-spin" /> : null}
                {submitting
                  ? 'Connecting…'
                  : tab === 'app-auth'
                    ? 'Continue to WordPress'
                    : 'Connect store'}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default ConnectWooCommerceModal;
